import React, { useEffect, useState, useMemo, useCallback } from 'react';
import {
  KatButton,
  KatModal,
  KatTable,
  KatSpinner,
  KatLink,
} from '@amzn/katal-react';
import { useGetStringVersionHistoryQuery } from 'src/generated/graphql';
import { formatSourceReviewDateTime } from 'src/components/common/util/TimestampHelpers';
import { useTranslation } from 'src/i18next-arb-shim/useTranslation';
import './StringVersionHistoryModal.scss';

type Props = {
  isOpen: boolean;
  onClose: () => void;
  stringKey: any;
};

/**
 * Column configuration for Version History table.
 */
const versionHistoryColumnConfig = [
  {
    property: 'updatedAt',
    className: 'version-history-col-updated-timestamp',
    labelKey: 'source-review-string-version-history-update-timestamp-column',
    hasPopover: true,
  },
  {
    property: 'sourceText',
    className: 'version-history-col-source-text',
    labelKey: 'source-review-string-version-history-source-text-column',
    hasPopover: true,
  },
  {
    property: 'contextNote',
    className: 'version-history-col-context-note',
    labelKey: 'source-review-string-version-history-context-note-column',
    hasPopover: true,
  },
  {
    property: 'updatedBy',
    className: 'version-history-col-updated-by',
    labelKey: 'source-review-string-version-history-updated-by-column',
    hasPopover: false,
  },
  {
    property: 'submittedForTranslation',
    className: 'version-history-col-submitted',
    labelKey:
      'source-review-string-version-history-submitted-for-translation-column',
    hasPopover: false,
  },
] as const;

/**
 * Modal component that displays the Version History of a string.
 * Shows historical versions including update timestamps, source text, context notes,
 * who made the updates and whether the version was submitted for translation.
 */
const StringVersionHistoryModal: React.FC<Props> = ({
  isOpen,
  onClose,
  stringKey,
}) => {
  const { t, i18n } = useTranslation();
  // Number of Version History records to fetch
  const [numberOfVersions, setNumberOfVersions] = useState(5);

  // Popover state for displaying full text content when cells are clicked
  const [popoverContent, setPopoverContent] = useState('');
  const [popoverPosition, setPopoverPosition] = useState({ top: 0, left: 0 });
  const [popoverActiveCell, setPopoverActiveCell] = useState<string | null>(
    null
  );

  // String Key parameter for the Version History query
  const stringKeyParam = {
    stringName: stringKey?.stringName,
    stringGroupName: stringKey?.stringGroupName,
    stringType: stringKey?.stringType,
    locale: stringKey?.locale,
    variant: stringKey?.variant?.map((v: any) => ({
      key: v.key,
      value: v.value,
    })),
  };

  // Query to fetch Version History
  const { data, loading, error, refetch } = useGetStringVersionHistoryQuery({
    variables: {
      params: {
        stringKey: stringKeyParam,
        numberOfVersions,
      },
    },
    // Skip query if modal is closed or no string key
    skip: !isOpen || !stringKey,
  });

  // Refetch data when modal opens or numberOfVersions changes
  // Reset popover and history size when modal closes
  useEffect(() => {
    if (isOpen && stringKey) {
      refetch();
    } else if (!isOpen) {
      setPopoverContent('');
      setPopoverActiveCell(null);
      setNumberOfVersions(5);
    }
  }, [isOpen, stringKey, refetch, numberOfVersions]);

  // Format version history records from query response for display
  const formatRowData = useCallback(
    (versions: any) => {
      if (!versions?.length) return [];
      return versions.map((version: any) => ({
        updatedAt: formatSourceReviewDateTime(
          version?.updatedTimeStamp,
          false,
          i18n.language
        ),
        sourceText: version?.sourceText || '',
        contextNote: version?.contextNote || '',
        updatedBy: version?.updatedBy || '',
        submittedForTranslation: version?.submittedForTranslation
          ? 'Yes'
          : 'No',
      }));
    },
    [i18n.language]
  );

  // Memoize formatted row data
  const formattedRowData = useMemo(() => {
    return formatRowData(data?.getStringVersionHistory?.versionHistory);
  }, [data?.getStringVersionHistory?.versionHistory, formatRowData]);

  // Handle table cell clicks for popover enabled columns
  const handleCellClick = (
    rowIndex: number,
    colIndex: number,
    event: React.MouseEvent<HTMLTableCellElement>
  ) => {
    const clickedCellId = `${rowIndex}-${colIndex}`;
    const columnProperty = versionHistoryColumnConfig[colIndex]?.property;

    if (versionHistoryColumnConfig[colIndex]?.hasPopover) {
      // Close popover if clicking the same cell with an active popover
      if (clickedCellId === popoverActiveCell) {
        setPopoverContent('');
        setPopoverActiveCell(null);
        return;
      }

      // Calculate popover position relative to modal container
      const clickedCell = event.currentTarget;
      const modalContainer = clickedCell.closest(
        '.string-version-history-modal-content'
      );
      if (modalContainer) {
        const cellRect = clickedCell.getBoundingClientRect();
        const containerRect = modalContainer.getBoundingClientRect();
        setPopoverPosition({
          top: cellRect.bottom - containerRect.top + 5,
          left: cellRect.left - containerRect.left,
        });
      }

      // Show popover with full text content
      const formattedRecord = formattedRowData[rowIndex];
      const popoverText = (formattedRecord as any)?.[columnProperty] || '';
      setPopoverContent(popoverText);
      setPopoverActiveCell(clickedCellId);
    }
  };

  // Handle open or close behaviour of popover on click events
  useEffect(() => {
    const handleGlobalClick = (event: MouseEvent) => {
      const clickedElement = event.target as HTMLElement;

      // Don't close if clicking inside the popover
      const isClickInsidePopover = clickedElement.closest('.popover-box');
      if (isClickInsidePopover) return;

      // Don't close if clicking on popover enabled cells
      const clickedCell = clickedElement.closest('td');
      if (clickedCell) {
        if (versionHistoryColumnConfig[clickedCell.cellIndex]?.hasPopover)
          return;
      }

      // Close popover for any other click
      setPopoverContent('');
      setPopoverActiveCell(null);
    };

    // Attach global click listener
    document.addEventListener('click', handleGlobalClick);
    // Cleanup listener on component unmount
    return () => document.removeEventListener('click', handleGlobalClick);
  }, []);

  // Loading State
  if (loading) {
    return (
      <KatModal
        title={t('source-review-string-version-history-modal-title')}
        visible={isOpen}
        onClose={onClose}
        className="string-version-history-modal"
      >
        <div className="string-version-history-loading-container">
          <KatSpinner size="small" />
          <div className="string-version-history-loading-text">
            {t('source-review-string-version-history-loading-text')}
          </div>
        </div>
        <div slot="footer">
          <KatButton
            label={t('source-review-string-version-history-close-button')}
            variant="primary"
            onClick={onClose}
          />
        </div>
      </KatModal>
    );
  }

  // Error State
  if (error) {
    return (
      <KatModal
        title={t('source-review-string-version-history-modal-title')}
        visible={isOpen}
        onClose={onClose}
        className="string-version-history-error-modal"
      >
        <div className="string-version-history-error-container">
          <div className="string-version-history-error-text">
            {t('source-review-string-version-history-error-text')}
          </div>
        </div>
        <div slot="footer">
          <KatButton
            label={t('source-review-string-version-history-close-button')}
            variant="primary"
            onClick={onClose}
          />
        </div>
      </KatModal>
    );
  }

  return (
    <KatModal
      visible={isOpen}
      onClose={onClose}
      title={t('source-review-string-version-history-modal-title')}
      className="string-version-history-modal"
    >
      <div
        className="string-version-history-modal-content"
        style={{ position: 'relative' }}
      >
        {/* Display string metadata - String ID */}
        <div className="string-details">
          <div className="string-detail-item">
            <strong>
              {t('source-review-string-version-history-string-id-label')}
            </strong>
            <span>{stringKey?.stringName || 'N/A'}</span>
          </div>
          {/* Display string metadata - String Type */}
          <div className="string-detail-item">
            <strong>
              {t('source-review-string-version-history-string-type-label')}
            </strong>
            <span>{stringKey?.stringType || 'N/A'}</span>
          </div>
          {/* Display string metadata - I18nDomain(for variants) */}
          {stringKey?.stringType === 'VARIANT' && (
            <div className="string-detail-item">
              <strong>
                {t('source-review-string-version-history-i18n-domain-label')}
              </strong>
              <span>
                {stringKey?.variant?.find((v: any) => v.key === 'i18nDomain')
                  ?.value || 'N/A'}
              </span>
            </div>
          )}
        </div>

        {/* Version History table */}
        <div className="version-history-table">
          <KatTable>
            <thead>
              <tr>
                {/* Render table headers from columnConfig */}
                {versionHistoryColumnConfig.map((col) => (
                  <th
                    key={col.property}
                    className={`version-history-table-header version-history-table-cell ${col.className}`}
                  >
                    {t(col.labelKey)}
                  </th>
                ))}
              </tr>
            </thead>
            <tbody>
              {/* Render table rows from formatted version history data */}
              {formatRowData(data?.getStringVersionHistory?.versionHistory).map(
                (row: any, rowIndex: number) => (
                  <tr key={rowIndex}>
                    {versionHistoryColumnConfig.map((col, colIndex) => {
                      const cellValue = row[col.property];
                      const cellClasses = [
                        'version-history-table-cell',
                        col.className,
                        col.hasPopover &&
                          'version-history-table-cell-clickable',
                      ]
                        .filter(Boolean)
                        .join(' ');
                      return (
                        <td
                          key={col.property}
                          // Handle clicks for popover enabled columns
                          onClick={
                            col.hasPopover
                              ? (e) => handleCellClick(rowIndex, colIndex, e)
                              : undefined
                          }
                          className={cellClasses}
                        >
                          {/* Render user alias as clickable link to phonetool */}
                          {col.property === 'updatedBy' && cellValue ? (
                            <KatLink
                              href={`https://phonetool.amazon.com/users/${cellValue}`}
                              label={cellValue}
                              target="_blank"
                            />
                          ) : (
                            cellValue
                          )}
                        </td>
                      );
                    })}
                  </tr>
                )
              )}
            </tbody>
          </KatTable>
        </div>

        {/* Dropdown to select number of versions to display */}
        <div className="history-size-selector">
          <span className="version-text">
            {t('source-review-string-version-history-history-size-label')}{' '}
          </span>
          <select
            value={numberOfVersions}
            onChange={(e) => setNumberOfVersions(parseInt(e.target.value))}
            className="version-dropdown"
          >
            <option value={5}>5</option>
            <option value={10}>10</option>
          </select>
        </div>

        {/* Popover to display full text content */}
        {popoverContent && (
          <div className="popover-overlay" style={{ pointerEvents: 'none' }}>
            <div
              className="popover-box"
              style={{
                top: popoverPosition.top,
                left: popoverPosition.left,
                pointerEvents: 'auto',
              }}
              onClick={(e) => e.stopPropagation()}
            >
              <div className="popover-arrow" />
              <pre className="popover-content">{popoverContent}</pre>
            </div>
          </div>
        )}
      </div>
      <div slot="footer">
        <KatButton
          label={t('source-review-string-version-history-close-button')}
          variant="primary"
          onClick={onClose}
        />
      </div>
    </KatModal>
  );
};

export default StringVersionHistoryModal;
